import { CalendarToday, DateRange } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  DESCRIPTION_CHAR_LIMIT,
  TOURNAMENT_NAME_CHAR_LIMIT,
  TOURNAMENT_PASSWORD_CHAR_LIMIT as PASSWORD_CHAR_LIMIT,
} from '../../../../../../corelogic/domain/constants/fieldLimits.constants';
import { Season } from '../../../../../../corelogic/domain/models/season.model';
import { TournamentUpsert } from '../../../../../../corelogic/domain/models/tournament.model';
import { formatDate } from '../../../../../../corelogic/domain/utils/date.utils';
import { ValidationResult } from '../../../../../../corelogic/domain/utils/realtimeSecurityValidation.utils';
import { checkIsBetweenSeasons } from '../../../../../../corelogic/domain/utils/tournament.utils';
import { SecureTextField } from '../../Shared/SecureTextField/SecureTextField';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      bgcolor: 'rgba(20, 20, 20, 0.95)',
      color: '#ffffff',
      borderRadius: 2,
      maxWidth: '700px',
      width: '90%',
      backgroundImage:
        'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7))',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
      border: '1px solid rgba(120, 120, 120, 0.4)',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    bgcolor: 'rgba(0, 0, 0, 0.7)',
    borderBottom: '1px solid rgba(204, 0, 0, 0.3)',
    p: 2,
  },
  title: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  closeButton: {
    color: '#cccccc',
    '&:hover': {
      color: '#ffffff',
      bgcolor: 'rgba(204, 0, 0, 0.2)',
    },
  },
  dialogContent: {
    p: 3,
  },
  formField: {
    mb: 2,
    '& .MuiInputLabel-root': {
      color: '#cccccc',
    },
    '& .MuiOutlinedInput-root': {
      color: '#ffffff',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(204, 0, 0, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#cc0000',
      },
    },
    '& .MuiInputBase-input': {
      bgcolor: 'rgba(0, 0, 0, 0.3)',
    },
    '& .MuiSelect-icon': {
      color: '#cccccc',
    },
  },
  sectionTitle: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
    color: '#cc0000',
    mt: 3,
    mb: 3,
    borderLeft: '3px solid #cc0000',
    pl: 1,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    mt: 3,
    gap: 2,
  },
  cancelButton: {
    color: '#cccccc',
    borderColor: '#cccccc',
    '&:hover': {
      borderColor: '#ffffff',
      bgcolor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  createButton: {
    background: 'linear-gradient(45deg, #990000 30%, #cc0000 90%)',
    color: '#ffffff',
    '&:hover': {
      background: 'linear-gradient(45deg, #770000 30%, #aa0000 90%)',
    },
    boxShadow: '0 3px 5px 2px rgba(204, 0, 0, .3)',
  },
  passwordSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 2,
  },
  lockIcon: {
    color: '#cc0000',
    fontSize: 'small',
  },
  required: {
    color: '#cc0000',
    ml: 0.5,
  },
  characterCount: {
    fontSize: '0.75rem',
    color: '#999999',
    textAlign: 'right',
    mt: 0.5,
  },
  characterCountWarning: {
    color: '#ff9900',
  },
  characterCountError: {
    color: '#ff0000',
  },
};

interface CreateTournamentModalProps {
  open: boolean;
  onClose: () => void;
  onCreateTournament: (tournament: TournamentUpsert) => void;
  seasons: Season[];
}

const CreateTournamentModal = ({
  open,
  onClose,
  onCreateTournament,
  seasons,
}: CreateTournamentModalProps) => {
  // States
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [password, setPassword] = useState('');
  const [seasonCount, setSeasonCount] = useState('1');
  const [startDateOption, setStartDateOption] = useState<
    'nextSeason' | 'today'
  >('nextSeason');
  const [startDate, setStartDate] = useState('');
  const [nextSeasonDate, setNextSeasonDate] = useState<string>('');
  const today = new Date();

  // Simplified security validation state
  const [fieldValidations, setFieldValidations] = useState<
    Record<string, ValidationResult>
  >({});

  // Check if today is between two seasons
  const isBetweenSeasons = checkIsBetweenSeasons(seasons, today);

  // Generate season count options from 1 to 10
  const seasonOptions = Array.from({ length: 10 }, (_, i) => ({
    value: String(i + 1),
    label: i === 0 ? '1 Season' : `${i + 1} Seasons`,
  }));

  // Find next upcoming season
  useEffect(() => {
    if (seasons && seasons.length > 0) {
      const upcomingSeasons = seasons
        .filter((season) => new Date(season.startDate) > today)
        .sort(
          (a, b) =>
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
        );

      if (upcomingSeasons.length > 0) {
        // Format date to YYYY-MM-DD for the input
        const nextDate = new Date(upcomingSeasons[0].startDate);
        setNextSeasonDate(nextDate.toISOString().split('T')[0]);

        // If next season option is selected, use this date
        if (startDateOption === 'nextSeason') {
          setStartDate(nextDate.toISOString().split('T')[0]);
        }
      }
    }
  }, [seasons, startDateOption]);

  // Handle date option change
  const handleStartDateOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value as 'nextSeason' | 'today';
    setStartDateOption(value);

    if (value === 'nextSeason' && nextSeasonDate) {
      setStartDate(nextSeasonDate);
    } else if (value === 'today') {
      // Format today's date as YYYY-MM-DD for the input value
      const todayFormatted = today.toISOString().split('T')[0];
      setStartDate(todayFormatted);
    }
  };

  // Generic secure change handler
  const handleSecureChange = (
    field: string,
    value: string,
    validation: ValidationResult,
  ) => {
    // Update form values
    switch (field) {
      case 'name':
        setName(value);
        break;
      case 'description':
        setDescription(value);
        break;
      case 'password':
        setPassword(value);
        break;
    }

    // Update validation state
    setFieldValidations((prev) => ({ ...prev, [field]: validation }));
  };

  // Check if there are any high-severity security issues
  const hasSecurityIssues = () => {
    return Object.values(fieldValidations).some((validation) =>
      validation.warnings.some((warning) => warning.severity === 'high'),
    );
  };

  // Form submission with validation
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation first
    if (!name.trim() || !password.trim()) {
      alert('Please fill all required fields');
      return;
    }

    // Don't submit if there are security issues
    if (hasSecurityIssues()) {
      return; // Security messages are already shown below fields
    }

    // Proceed with creation
    const startDate =
      startDateOption === 'today' ? today : new Date(nextSeasonDate);

    const newTournament: TournamentUpsert = {
      name: name.trim(),
      description: description.trim(),
      startDate,
      numberOfSeasons: seasonCount === 'all' ? 999 : parseInt(seasonCount),
      private: true,
      password: password.trim(),
    };

    onCreateTournament(newTournament);
    resetForm();
    onClose(); // Close modal after successful creation
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setPassword('');
    setSeasonCount('1');
    // Reset validation state
    setFieldValidations({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <DialogTitle sx={styles.dialogTitle}>
        <Typography sx={styles.title}>
          <EmojiEventsIcon sx={{ color: '#cc0000' }} />
          Create Tournament
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography sx={styles.sectionTitle}>
                Tournament Details
              </Typography>
            </Grid>

            {/* Name */}
            <Grid item xs={12}>
              <SecureTextField
                label="Tournament Name"
                value={name}
                onChange={(value, validation) =>
                  handleSecureChange('name', value, validation)
                }
                fieldType="name"
                fieldName="Tournament Name"
                required
                fullWidth
                sx={styles.formField}
                showCharacterCounter={true}
                inputProps={{ maxLength: TOURNAMENT_NAME_CHAR_LIMIT }}
                InputProps={{
                  endAdornment: <Typography sx={styles.required}>*</Typography>,
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <SecureTextField
                label="Description"
                value={description}
                onChange={(value, validation) =>
                  handleSecureChange('description', value, validation)
                }
                fieldType="description"
                fieldName="Tournament Description"
                multiline
                rows={3}
                fullWidth
                sx={styles.formField}
                placeholder="Describe your tournament (optional)"
                showCharacterCounter={true}
                inputProps={{ maxLength: DESCRIPTION_CHAR_LIMIT }}
              />
            </Grid>

            {/* Start date and season count */}
            <Grid item xs={12}>
              <Typography sx={styles.sectionTitle}>
                Tournament timing
              </Typography>
              <RadioGroup
                value={startDateOption}
                onChange={handleStartDateOptionChange}
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="today"
                  disabled={isBetweenSeasons}
                  control={<Radio />}
                  sx={{ alignItems: 'flex-start' }}
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <CalendarToday sx={{ color: '#cc0000' }} />
                        <Typography>
                          Start today ({formatDate(today)})
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: '#aaaaaa', ml: 4 }}
                      >
                        Note: Fights that have already happened won't be
                        included in the tournament's point calculation
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="nextSeason"
                  control={<Radio />}
                  sx={{ alignItems: 'center' }}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DateRange sx={{ color: '#cc0000' }} />
                      <Typography>
                        Start with next season
                        {nextSeasonDate &&
                          ` (${formatDate(new Date(nextSeasonDate))})`}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </Grid>

            {/* Start date */}
            <Grid item xs={12} sm={6}>
              {startDateOption === 'today' ? (
                <TextField
                  label="Start Date"
                  type="date"
                  value={today.toISOString().split('T')[0]}
                  disabled
                  fullWidth
                  sx={styles.formField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              ) : (
                <TextField
                  label="Start Date"
                  type="date"
                  value={nextSeasonDate}
                  disabled
                  fullWidth
                  sx={styles.formField}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </Grid>

            {/* Season count */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={styles.formField}>
                <InputLabel>Number of Seasons</InputLabel>
                <Select
                  value={seasonCount}
                  onChange={(e) => setSeasonCount(e.target.value)}
                  label="Number of Seasons"
                >
                  {seasonOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Password (required) - CRITICAL: Tournament passwords are stored in DB! */}
            <Grid item xs={12}>
              <Typography sx={styles.sectionTitle}>Security</Typography>
              <Box sx={styles.passwordSection}>
                <LockIcon sx={styles.lockIcon} />
                <Typography variant="body2" sx={{ color: '#cccccc' }}>
                  This tournament is private and requires a password
                </Typography>
              </Box>
              <SecureTextField
                label="Access Password"
                value={password}
                onChange={(value, validation) =>
                  handleSecureChange('password', value, validation)
                }
                fieldType="tournament_password"
                fieldName="Tournament Password"
                required
                fullWidth
                type="password"
                sx={styles.formField}
                placeholder="Create a password for your tournament"
                inputProps={{ maxLength: PASSWORD_CHAR_LIMIT }}
                InputProps={{
                  endAdornment: <Typography sx={styles.required}>*</Typography>,
                }}
              />
            </Grid>
          </Grid>

          <Box sx={styles.buttonContainer}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={styles.createButton}
              disabled={hasSecurityIssues()}
            >
              Create Tournament
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTournamentModal;
